import './plots.css';

import React from 'react';

import { PositionData } from '../../types/position';
import { ProgressCircle } from '../display/visualization/ProgressCircle';
import { getIndexColor } from '../../utils/colorUtils';
import { translate } from '../../../common/language/translations';


interface PositionFieldPlotProps {
  positions: PositionData[];
  language: string;
  showConfidence?: boolean;
}


const positionPercentageThreshold = 0.03;


export const PositionFieldPlot: React.FC<PositionFieldPlotProps> = ({
  positions,
  language,
  showConfidence,
}) => {

  const width = 420;
  const height = 400;
  const centerX = width / 2;
  const wideOffset = 140;
  const leftCenterX = centerX - wideOffset;
  const rightCenterX = centerX + wideOffset;


  const positionToCenterCoordinates: { [key: string]: number[] } = {
    'S': [centerX, 55],
    'LW': [leftCenterX, 90],
    'RW': [rightCenterX, 90],
    'CAM': [centerX, 115],
    'CM': [centerX, 175],
    'CDM': [centerX, 235],
    'LB': [leftCenterX, 280],
    'RB': [rightCenterX, 280],
    'CB': [centerX, 295],
    'GK': [centerX, 340],
  };


  const fapiPositionToFifaPosition: { [key: string]: number[] } = {
    'G': [centerX, 340],
    'D': [centerX, 295],
    'M': [centerX, 175],
    'F': [centerX, 55],
  };


  const getPositionRadius = (percentage: number): number => {
    return 15 + 20 * percentage;
  };

  const getFontSize = (radius: number): number => {
    if (radius > 23) return 14;
    if (radius > 21) return 13;
    if (radius > 19) return 12;
    if (radius > 17) return 11;
    return 10;
  };


  const strokeColor = '#484d59';
  const strokeWidth = 1;


  return (
    <svg className='player-view-svg-plot' viewBox={'0 0 ' + width + ' ' + height} preserveAspectRatio='xMidYMid meet'>

      {/* <rect width='420' height='400' style={{ fill: 'none', stroke: '#ffffff88', strokeWidth: 4 }} /> */}

      {/* Main outline */}
      <rect
        x="10"
        y="10"
        width="400"
        height="380"
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
      />

      {/* Goal area at bottom */}
      <line x1="140" y1="340" x2="280" y2="340" stroke={strokeColor} strokeWidth={strokeWidth}/>
      <line x1="140" y1="340" x2="140" y2="390" stroke={strokeColor} strokeWidth={strokeWidth}/>
      <line x1="280" y1="340" x2="280" y2="390" stroke={strokeColor} strokeWidth={strokeWidth}/>

      {/* Penalty area */}
      <line x1="80" y1="290" x2="340" y2="290" stroke={strokeColor} strokeWidth={strokeWidth}/>
      <line x1="80" y1="290" x2="80" y2="390" stroke={strokeColor} strokeWidth={strokeWidth}/>
      <line x1="340" y1="290" x2="340" y2="390" stroke={strokeColor} strokeWidth={strokeWidth}/>

      {/* Penalty arc - even smaller segment */}
      <path
        d="M 170,290 A 43,43 0 0 1 250,290"
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
      />

      {/* Half circle at top */}
      <path
        d="M 150,10 A 50,50 0 0 0 270,10"
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
      />

      {/* Position indicators */}
      {positions.map(position => {

        const percentage = position.percentage;
        if (percentage <= positionPercentageThreshold) return null;

        const isFapiPosition = position.position in fapiPositionToFifaPosition;

        const [x, y] = isFapiPosition
          ? fapiPositionToFifaPosition[position.position]
          : positionToCenterCoordinates[position.position];

        let positionString = translate(position.position, language);
        if (isFapiPosition) positionString = positionString[0];


        const radius = getPositionRadius(percentage);
        const size = radius * 2;

        return (
          <foreignObject
            key={position.position}
            x={x - radius}
            y={y - radius}
            width={size}
            height={size}
          >
            <div className='position-field-plot-position'>
              <ProgressCircle
                size={size}
                progress={showConfidence ? Math.round(percentage * 100) : 0}
                color={getIndexColor(percentage * 10, 60)}
                trailColor={'#4e5360'}
                duration={0.6}
                fontSize={getFontSize(radius)}
                value={Math.round(percentage * 100)}
                position={positionString}
              />
            </div>
          </foreignObject>
        );
      })}
    </svg>
  );
};
